import {
  DOLL_SLOTS,
  type Attributes,
  type CharacterPayload,
  type DerivedStats,
  type DollSlot,
  type InventoryItem,
  type StatRequirements,
} from '../net/protocol';

/**
 * Pure logic behind the character sheet: slot naming, requirement checks, and
 * which stats are live versus merely tallied.
 *
 * Kept out of the component for the same reason `stash.ts` is — this is where
 * the rules live, and rules deserve tests that do not need a DOM.
 */

/** The label under each paper-doll square. */
const SLOT_LABELS: Record<DollSlot, string> = {
  HEAD: 'Head',
  AMULET: 'Amulet',
  CHEST: 'Chest',
  GLOVES: 'Gloves',
  BELT: 'Belt',
  LEGS: 'Legs',
  BOOTS: 'Boots',
  RING_1: 'Ring I',
  RING_2: 'Ring II',
  MAIN_HAND: 'Main Hand',
  OFF_HAND: 'Off Hand',
};

export function slotLabel(slot: DollSlot): string {
  return SLOT_LABELS[slot] ?? slot;
}

/**
 * Which doll slots a carried item could go in, in preference order.
 *
 * Mirrors `Base.Fits` on the server. The client only uses this to offer the
 * player somewhere to put things — the server re-derives it and refuses anything
 * that does not fit, so a wrong answer here is a UI bug, never an exploit.
 *
 * `slot` is the item's *category* (the server's item.Slot), not a doll slot. The two
 * vocabularies differ in exactly one place — one `RING` category feeds two fingers — and
 * every other category is spelled the same as the socket it goes in.
 *
 * ── The bug this had, for its whole life ────────────────────────────────────
 *
 * There was a `case 'WEAPON'` and no `case 'MAIN_HAND'`. **No category is called
 * `WEAPON`**: `config.Slots` names ten and the weapon one is `MAIN_HAND`, so every sword,
 * bow, staff and dagger fell through to `default` and returned no candidate slot at all.
 * A weapon in the bag was therefore marked *cannot be worn*, its tooltip said "nothing you
 * have can wear this", and clicking it did nothing — for every weapon in the game.
 *
 * It survived because the old carried list was a row of text where an unwearable item is
 * only slightly dimmer, and because the specs used a fixture whose default slot was the
 * same invented `WEAPON`. It became obvious the moment the grid drew a dashed border round
 * two of seven test items in a real browser. `TestEveryItemCategoryHasASocket` below is the
 * guard: it walks the server's own vocabulary rather than a hand-written list.
 */
export function slotsFor(item: InventoryItem): DollSlot[] {
  switch (item.slot) {
    case 'HEAD':
      return ['HEAD'];
    case 'CHEST':
      return ['CHEST'];
    case 'LEGS':
      return ['LEGS'];
    case 'BOOTS':
      return ['BOOTS'];
    case 'GLOVES':
      return ['GLOVES'];
    case 'BELT':
      return ['BELT'];
    case 'AMULET':
      return ['AMULET'];
    case 'RING':
      return ['RING_1', 'RING_2'];
    case 'OFF_HAND':
      return ['OFF_HAND'];
    case 'MAIN_HAND':
      // Main hand, always. A dagger may also go in the off-hand, but that flag
      // lives on the server's base table and is not on the wire, so the panel
      // offers the main hand and lets an explicit off-hand placement be judged
      // by the server. Getting this wrong is a UI inconvenience, never an
      // exploit — every placement is re-derived server-side.
      return ['MAIN_HAND'];
    default:
      return [];
  }
}

/**
 * Every item category the server can send, mirroring `config.Slots`.
 *
 * A hand-copy, for the reason `DOLL_SLOTS` is one: the wire carries strings and this is
 * the client's list of the ones it understands. It exists so `slotsFor` can be checked
 * against the whole vocabulary rather than against the cases somebody remembered — which
 * is what let `MAIN_HAND` go unhandled for the life of the file.
 */
export const ITEM_CATEGORIES = [
  'HEAD',
  'CHEST',
  'LEGS',
  'BOOTS',
  'GLOVES',
  'BELT',
  'AMULET',
  'RING',
  'MAIN_HAND',
  'OFF_HAND',
] as const;

/**
 * Where a double-click should send an item.
 *
 * Prefers an empty slot over occupying one, so a second ring goes on the free
 * finger rather than replacing the first.
 */
export function preferredSlot(
  item: InventoryItem,
  occupied: (slot: DollSlot) => boolean,
): DollSlot | null {
  const candidates = slotsFor(item);
  if (candidates.length === 0) return null;

  const free = candidates.find((slot) => !occupied(slot));
  return free ?? candidates[0] ?? null;
}

/** True when the character's attributes clear an item's gate. */
export function meetsRequirements(total: Attributes, req: StatRequirements): boolean {
  return (
    total.str >= req.str && total.dex >= req.dex && total.int >= req.int && total.end >= req.end
  );
}

/** Which attributes fall short, for a tooltip that says why. */
export function unmetRequirements(total: Attributes, req: StatRequirements): string[] {
  const short: string[] = [];
  if (total.str < req.str) short.push(`${req.str} STR`);
  if (total.dex < req.dex) short.push(`${req.dex} DEX`);
  if (total.int < req.int) short.push(`${req.int} INT`);
  if (total.end < req.end) short.push(`${req.end} END`);
  return short;
}

/** True when a requirement demands anything at all. */
export function hasRequirements(req: StatRequirements): boolean {
  return req.str > 0 || req.dex > 0 || req.int > 0 || req.end > 0;
}

/** Formats a requirement as "45 STR · 18 DEX", or empty when there is no gate. */
export function requirementText(req: StatRequirements): string {
  const parts: string[] = [];
  if (req.str > 0) parts.push(`${req.str} STR`);
  if (req.dex > 0) parts.push(`${req.dex} DEX`);
  if (req.int > 0) parts.push(`${req.int} INT`);
  if (req.end > 0) parts.push(`${req.end} END`);
  return parts.join(' · ');
}

/** One row of the stat readout. */
export interface StatRow {
  label: string;
  value: string;
  /**
   * False when the number is a real sum of real gear that nothing in the
   * simulation reads yet. The sheet marks these rather than implying an effect —
   * no design document specifies how armour, resistances or crit should apply,
   * and inventing a formula would set balance by accident.
   */
  live: boolean;
}

/** Rounds to at most one decimal, without trailing ".0". */
export function num(v: number): string {
  return (Math.round(v * 10) / 10).toString();
}

/**
 * The offensive readout.
 *
 * Attack damage and swing time are what the server actually resolves combat
 * with; the rest are tallied.
 */
export function offenceRows(d: DerivedStats): StatRow[] {
  return [
    { label: 'Attack Damage', value: num(d.attackDamage), live: true },
    { label: 'Swing Time', value: `${num(d.attackCooldown)}s`, live: true },
    { label: 'Physical Power', value: num(d.physicalAttackPower), live: false },
    { label: 'Magic Power', value: num(d.magicAttackPower), live: false },
    { label: 'Attack Rating', value: num(d.attackRating), live: false },
    // Live since COMBAT.md specified the curve. The server rolls against
    // exactly this number.
    { label: 'Critical Chance', value: `${num(d.critChancePct)}%`, live: true },
    { label: 'Critical Damage', value: `+${d.critDamagePct}%`, live: true },
    { label: 'Spell Critical Chance', value: `${num(d.spellCritChancePct)}%`, live: false },
    { label: 'Increased Spell Damage', value: `${d.spellDamagePct}%`, live: false },
  ];
}

/** The defensive readout. */
export function defenceRows(d: DerivedStats): StatRow[] {
  return [
    { label: 'Maximum Health', value: num(d.maxHealth), live: true },
    { label: 'Movement Speed', value: num(d.moveSpeed), live: true },
    // Armour is a *score*, and the score alone tells a player nothing — so the sheet
    // shows what it buys beside it. Both live since the mitigation formula landed.
    { label: 'Armour', value: num(d.armour), live: true },
    { label: 'Physical Mitigation', value: `${num(d.physicalMitigationPct)}%`, live: true },
    // **Toughness: effective health against a physical hit.** The one row that answers "is +100 HP
    // better than +50 armour", because mitigation soft-caps while effective health rises linearly —
    // so a point of armour is worth the same at 3000 as at 100, which a mitigation percentage
    // actively hides. Labelled *physical* because mitigation is per damage type and this is the type
    // most hits in the game are; it counts armour and the resistances and none of the three
    // defensive stats the simulation still ignores.
    { label: 'Toughness (physical)', value: num(d.toughness), live: true },
    { label: 'Fire Resistance', value: `${d.fireResistPct}%`, live: false },
    { label: 'Cold Resistance', value: `${d.coldResistPct}%`, live: false },
    { label: 'Lightning Resistance', value: `${d.lightningResistPct}%`, live: false },
    // Stamina and mana are live now: skills spend them, and they regenerate.
    { label: 'Maximum Stamina', value: num(d.maxStamina), live: true },
    { label: 'Stamina Regen', value: `${num(d.staminaRegen)}/s`, live: true },
    { label: 'Maximum Mana', value: num(d.maxMana), live: true },
    { label: 'Mana Regen', value: `${num(d.manaRegen)}/s`, live: true },
    { label: 'Evasion', value: num(d.evasion), live: false },
    { label: 'Block Chance', value: `${d.blockChancePct}%`, live: false },
    { label: 'Health Regen', value: `${d.healthRegen}/s`, live: false },
  ];
}

/** The four attributes, split into base and what gear added. */
export interface AttributeRow {
  key: 'str' | 'dex' | 'int' | 'end';
  label: string;
  /** What this attribute does, one line, from CLASSES-ATTRIBUTES.md. */
  effect: string;
  base: number;
  gear: number;
  total: number;
}

export function attributeRows(c: CharacterPayload): AttributeRow[] {
  const { base, gear, total } = c.attributes;
  return [
    {
      key: 'str',
      label: 'Strength',
      effect: '+1.5 physical power · heavy armour and two-handers',
      base: base.str,
      gear: gear.str,
      total: total.str,
    },
    {
      key: 'dex',
      label: 'Dexterity',
      effect: '+2 attack rating · +0.1% critical chance',
      base: base.dex,
      gear: gear.dex,
      total: total.dex,
    },
    {
      key: 'int',
      label: 'Intelligence',
      effect: '+2 magic power · +5 maximum mana',
      base: base.int,
      gear: gear.int,
      total: total.int,
    },
    {
      key: 'end',
      label: 'Endurance',
      effect: '+10 maximum health · +2 stamina',
      base: base.end,
      gear: gear.end,
      total: total.end,
    },
  ];
}

/** How many of the eleven slots are filled. */
export function equippedCount(c: CharacterPayload): number {
  return DOLL_SLOTS.filter((slot) => {
    const worn = c.equipment[slot];
    // A two-hander's virtual link is not a twelfth item; it is the main hand
    // being shown twice.
    return worn != null && !worn.isVirtualLink;
  }).length;
}
